import { apiClient } from '../../../api/client'
import type {
  TodoList,
  TodoListUpsert,
  TodoShare,
  TodoShareUserOption,
  TodoTask,
  TodoTaskDocument,
  TodoTaskUpsert,
} from '../types'

const LISTS = '/management/todo-lists'
const TASKS = '/management/todo-tasks'

/**
 * Notlarım uçları (113).
 *
 * <p><b>Sayfalama yoktur</b> — ölçek kişiseldir (kullanıcı başına onlarca liste, liste başına
 * yüzlerce madde üst sınır) ve "Tamamlananlar" açılır/kapanır bölümü sayfalamayla çelişirdi.
 *
 * <p>Uçlar kullanıcının **göremediği** bir kayıt için `404`, **görüp de sahibi olmadığı** bir kayda
 * yazma denemesinde `403` döner. İkisi ayrı: 404 kaydın varlığını sızdırmaz, 403 ise paylaşılan
 * kullanıcıya "bu senin değil" der.
 */

/** Kendi listeleri + kendisiyle paylaşılanlar; kendi listeleri önce, sonra Türkçe alfabeyle ad. */
export async function getTodoLists(): Promise<TodoList[]> {
  const { data } = await apiClient.get<TodoList[]>(LISTS)
  return data
}

export async function createTodoList(body: TodoListUpsert): Promise<TodoList> {
  const { data } = await apiClient.post<TodoList>(LISTS, body)
  return data
}

/** Yeniden adlandırma — yalnız sahip; `modDate` zorunlu (optimistic lock). */
export async function updateTodoList(id: number, body: TodoListUpsert): Promise<TodoList> {
  const { data } = await apiClient.put<TodoList>(`${LISTS}/${id}`, body)
  return data
}

/** Soft-delete — liste + maddeleri + paylaşımları. */
export async function deleteTodoList(id: number): Promise<void> {
  await apiClient.delete(`${LISTS}/${id}`)
}

export async function getTodoTasks(listId: number): Promise<TodoTask[]> {
  const { data } = await apiClient.get<TodoTask[]>(`${LISTS}/${listId}/tasks`)
  return data
}

export async function createTodoTask(listId: number, body: TodoTaskUpsert): Promise<TodoTask> {
  const { data } = await apiClient.post<TodoTask>(`${LISTS}/${listId}/tasks`, body)
  return data
}

export async function updateTodoTask(id: number, body: TodoTaskUpsert): Promise<TodoTask> {
  const { data } = await apiClient.put<TodoTask>(`${TASKS}/${id}`, body)
  return data
}

/**
 * Tamamlama işareti. **`modDate` göndermez** — sunucu bu uçta optimistic lock uygulamaz (tek alanlı,
 * idempotent geçiş; bayat sürüm yüzünden 409 vermek kullanıcıya "kutu çalışmıyor" hissi verirdi).
 */
export async function changeTodoTaskStatus(id: number, statusId: number): Promise<TodoTask> {
  const { data } = await apiClient.put<TodoTask>(`${TASKS}/${id}/status`, {
    todoTaskStatusId: statusId,
  })
  return data
}

/**
 * 124 — madde yorumu. Boş metin yorumu **kaldırır** (ayrı silme ucu yok). `modDate` zorunlu: paylaşılan
 * listede iki kişi aynı yorumu yazarsa sonraki 409 alır, öncekinin metnini sessizce ezmez.
 */
export async function updateTodoTaskDescription(
  id: number,
  description: string,
  modDate: string,
): Promise<TodoTask> {
  const { data } = await apiClient.put<TodoTask>(`${TASKS}/${id}/description`, {
    description,
    modDate,
  })
  return data
}

export async function deleteTodoTask(id: number): Promise<void> {
  await apiClient.delete(`${TASKS}/${id}`)
}

/**
 * Maddenin dosya ekleri (meta) — 123. İçerik değil: indirme/önizleme ortak ek ucundan
 * (`/management/attachments/{id}`) yapılır.
 */
export async function getTodoTaskAttachments(id: number): Promise<TodoTaskDocument[]> {
  const { data } = await apiClient.get<TodoTaskDocument[]>(`${TASKS}/${id}/attachments`)
  return data
}

/** Bir veya çok dosya ekler (multipart; alan adı `file`). Görev ve prensip kararıyla aynı kalıp. */
export async function uploadTodoTaskAttachments(id: number, files: File[]): Promise<void> {
  const form = new FormData()
  files.forEach((f) => form.append('file', f))
  await apiClient.post(`${TASKS}/${id}/attachments`, form)
}

/** Dosyayı kaldırır (soft-delete). */
export async function deleteTodoTaskAttachment(id: number, attachmentId: number): Promise<void> {
  await apiClient.delete(`${TASKS}/${id}/attachments/${attachmentId}`)
}

/** "Listeyi Görebilenler" — yalnız sahip çağırabilir; paylaşılan kullanıcı 403 alır. */
export async function getTodoShares(listId: number): Promise<TodoShare[]> {
  const { data } = await apiClient.get<TodoShare[]>(`${LISTS}/${listId}/shares`)
  return data
}

/**
 * Çoklu paylaşım ekleme — seçilen kişiler **tek istekte** gönderilir.
 *
 * <p>Sunucu hepsi-ya-hiç davranır: geçersiz tek bir kimlik (pasif kullanıcı, zaten ekli kişi)
 * isteğin tamamını reddeder. Bu bilinçlidir — bir kısmını yazıp gerisini sessizce atlamak, ekranda
 * "eklendi" görünürken kişinin listeyi görememesi demek olurdu.
 */
export async function addTodoShares(listId: number, userIds: number[]): Promise<TodoShare[]> {
  const { data } = await apiClient.post<TodoShare[]>(`${LISTS}/${listId}/shares`, { userIds })
  return data
}

/**
 * Paylaşım pop-up'ının kişi araması.
 *
 * <p>`listId` **gönderilir**: sunucu o listede zaten paylaşılan kişileri ve liste sahibini sonuçtan
 * eler. Eleme istemcide yapılsaydı iki yerde iki ölçüt olurdu ve biri değiştiğinde öteki bayatlardı.
 */
export async function searchTodoShareUsers(
  q: string,
  listId: number,
): Promise<TodoShareUserOption[]> {
  const { data } = await apiClient.get<TodoShareUserOption[]>(`${LISTS}/users`, {
    params: { q, listId },
  })
  return data
}

/**
 * Erişimi kaldırır — soft-delete, yalnız sahip.
 *
 * <p>Yolda **paylaşım kimliği değil, kullanıcı kimliği** taşınır: kaldırmanın anlamı "bu kişi
 * listeyi artık görmesin"dir. Veritabanı tekilliği zorlamadığı için (analist kararı) aynı kişinin
 * birden çok satırı olabilir; satır bazlı bir uç erişimi yarım kapatmayı mümkün kılardı.
 */
export async function removeTodoShare(listId: number, userId: number): Promise<void> {
  await apiClient.delete(`${LISTS}/${listId}/shares/${userId}`)
}
